// Component type definitions for structured UI components

export interface QRCode {
  componentType: 'qrcode';
  content: {
    header?: string;
    imageLink?: string;
  };
}

export interface FaqAnswersComponent {
  componentType: 'faqAnswersComponent';
  content: {
    title: string;
    message: string;
  };
}

export interface OptionsDisplay {
  componentType: 'optionsDisplay';
  content: {
    options: string[];
  };
}

export interface OptionsSummaryCard {
  componentType: 'optionsSummaryCard';
  content: {
    title: string;
    options: Array<{
      text: string;
      checked?: boolean;
    }>;
  };
}

export interface UsageCard {
  componentType: 'usageCard';
  content: {
    title: string;
    metrics: Array<{
      icon: 'clock' | 'devices' | 'video' | 'data';
      value: string;
      description: string;
    }>;
  };
}

export interface OffersCard {
  componentType: 'offersCard';
  content: Array<{
    header: string;
    title: string;
    description: string;
    profileImage?: string;
    options: Array<{
      key: string;
      value: string;
    }>;
    discountAmount: string;
    actualAmount: string;
    currency: string;
    cta: string;
    period?: string;
  }>;
}

export interface OffersComparisonCard {
  componentType: 'offersComparisonCard';
  content: Array<{
    header: string;
    title: string;
    description: string;
    options: Array<{
      bundleName: string;
      amount: string;
      currency: string;
    }>;
    cta: string;
  }>;
}

export interface SelectedPlanCard {
  componentType: 'selectedPlanCard';
  content: Array<{
    header: string;
    title: string;
    description: string;
    options: Array<{
      key: string;
      value: string;
    }>;
    arrivalText: string;
    arrivalSubText: string;
    paymentDetails: string;
    discountAmount: string;
    actualAmount: string;
    currency: string;
  }>;
}

export interface BillSummary {
  componentType: 'billSummary';
  content: Array<{
    header: string;
    dueAmount: string;
    currency: string;
    month: string;
    regularCost: string;
    subscriptionExtras: string;
    proRataCost: string;
    packageDetail: string;
  }>;
}

export interface ProRataCard {
  componentType: 'proRataCard';
  content: Array<{
    header: string;
    dueDays: string;
    proRataCost: string;
    currency: string;
    regularMonthlyCost: string;
    billingStartDate: string;
    orderPlacedDate: string;
  }>;
}

export interface CurrentPlanCard {
  componentType: 'currentPlanCard';
  content: Array<{
    header?: string;
    title?: string;
    description?: string;
    options?: Array<{
      key: string;
      value: string;
      icon?: string;
    }>;
    actualAmount?: string;
    originalAmount?: string;
    currency?: string;
  }>;
}

export interface OrderArrivalCard {
  componentType: 'orderArrivalCard';
  content: Array<{
    arrivalDate: string;
    arrivalTime: string;
  }>;
}

export interface WelcomeMessageComponent {
  componentType: 'welcomeMessage';
  content: {};
}

// Multi-component response for arrays of structured components
export interface MultiComponentResponse {
  isMultiComponent: true;
  components: SingleStructuredComponent[];
}

// Union type for single structured components
export type SingleStructuredComponent = 
  | QRCode
  | FaqAnswersComponent 
  | OptionsDisplay 
  | OptionsSummaryCard 
  | UsageCard 
  | OffersCard 
  | OffersComparisonCard 
  | SelectedPlanCard 
  | CurrentPlanCard
  | BillSummary 
  | ProRataCard 
  | OrderArrivalCard
  | WelcomeMessageComponent;

// Union type for all structured components (single or multi)
export type StructuredComponent = SingleStructuredComponent | MultiComponentResponse;

// Type guard to check if response contains structured component
export function isStructuredComponent(data: unknown): data is StructuredComponent {
  return (
    data !== null &&
    typeof data === 'object' && 
    (
      ('componentType' in (data as Record<string, unknown>) && 'content' in (data as Record<string, unknown>)) ||
      ('isMultiComponent' in (data as Record<string, unknown>) && 'components' in (data as Record<string, unknown>))
    )
  );
}

// Type guard to check if response is multi-component
export function isMultiComponentResponse(data: unknown): data is MultiComponentResponse {
  return (
    data !== null &&
    typeof data === 'object' && 
    'isMultiComponent' in (data as Record<string, unknown>) &&
    'components' in (data as Record<string, unknown>) &&
    (data as MultiComponentResponse).isMultiComponent === true
  );
}
